/* eslint-disable no-unused-expressions */

import { withHTMLComponent } from '@updater-legacy/components/custom-element';
import { KEY_CODES, KEY_CODE_VALUES, WEEKDAYS } from './constants';
import * as utils from './utils';

const html = `
<header>
  <label id="calendarLabel"></label>

  <div id="controls">
    <button type="button" id="previousMonth" aria-label="Previous Month">
      <
    </button>
    <button type="button" id="nextMonth" aria-label="Next Month">></button>
  </div>
</header>

<table role="grid" aria-labelledby="calendarLabel">
  <thead>
    <tr>
      <th>S</th>
      <th>M</th>
      <th>T</th>
      <th>W</th>
      <th>T</th>
      <th>F</th>
      <th>S</th>
    </tr>
  </thead>

  <tbody></tbody>
</table>
`;

const css = `
:host {
  display: block;
}

label,
button,
#controls {
  font-family: inherit;
}

header {
  display: flex;
  align-items: center;
  margin: 0.5em 0;
}

label {
  color: #404041;
  font-size: 1em;
  margin-left: 0.8em;
}

button {
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  width: 2.5em;
  height: 2.5em;
  color: #404041;
  font-size: 0.8em;
  padding: 0 /* Needed for iOS Safari */
}

button:focus,
  button:hover {
    background: #efefef;
  }

button[aria-disabled='true'],
  button[disabled] {
    color: #e4e4e5;
    cursor: default;
    background: none;
  }

#controls {
  margin-left: auto
}

#controls button {
    font-weight: 600;
  }

[role='grid'] {
  width: 100%;
  border-collapse: collapse
}

[role='grid'] th {
    color: #bababb;
    border-bottom: #efefef 0.0625em solid;
    padding-bottom: 0.5em;
    font-size: 1em;
  }

[role='grid'] tbody td {
      padding: 0.125em;
    }

[role='grid'] tbody tr:first-child td {
      padding-top: 0.375em;
    }

[role='grid'] button {
    display: block;
    margin: 0 auto
  }

[role='grid'] button.today {
      border: 0.0625em solid #5590ad;
    }

[role='grid'] button[aria-selected='true'] {
      color: white;
      background: #5590ad;
    }

`;

const properties = ['value', 'min', 'max'];
class Datepicker extends withHTMLComponent(css, html)() {
  // The calendar month date (stored as `YYYY-MM-01`)
  // Used to determine which year / month to show on the calendar
  _calendarMonthDate = null;

  // The specific enabled dates
  _enabledDates = null;

  // The specific disabled dates
  _disabledDates = null;

  static observedAttributes = properties;

  static reflectedAttributes = properties;

  constructor() {
    super();

    this._label = this.shadowRoot.querySelector('label');
    this._dates = this.shadowRoot.querySelector('[role="grid"] tbody');
    this._prevMonthBtn = this.shadowRoot.querySelector('#previousMonth');
    this._nextMonthBtn = this.shadowRoot.querySelector('#nextMonth');
  }

  connected() {
    this._dates.addEventListener('click', this._handleDateSelected);
    this._dates.addEventListener('keydown', this._handleDatesKeydown);
    this._prevMonthBtn.addEventListener('click', this._handlePrevMonthClick);
    this._nextMonthBtn.addEventListener('click', this._handleNextMonthClick);

    this._render(utils.valueOrToday(this.value));
  }

  disconnected() {
    this._dates.removeEventListener('click', this._handleDateSelected);
    this._dates.removeEventListener('keydown', this._handleDatesKeydown);
    this._prevMonthBtn.removeEventListener('click', this._handlePrevMonthClick);
    this._nextMonthBtn.removeEventListener('click', this._handleNextMonthClick);
  }

  set enabledDates(value) {
    this._enabledDates = value;
    this._render(utils.valueOrToday(this.value));
  }

  get enabledDates() {
    return this._enabledDates;
  }

  set disabledDates(value) {
    this._disabledDates = value;
    this._render(utils.valueOrToday(this.value));
  }

  get disabledDates() {
    return this._disabledDates;
  }

  updated({ value }) {
    this._render(utils.valueOrToday(value));
  }

  _createGridCells(dateString) {
    const { value, min, max, enabledDates, disabledDates } = this;
    const { year, month } = utils.parseDateStringToComponents(dateString);
    const { paddedDaysInMonth, firstWeekday } = utils.getMonthInfo(year, month);
    const numOfRows = Math.ceil(paddedDaysInMonth / 7);

    return Array.from({ length: numOfRows }).map((_, weekIndex) => {
      const row = document.createElement('tr');
      WEEKDAYS.forEach((weekday, weekdayIndex) => {
        const cell = document.createElement('td');
        const cellNum = weekdayIndex + weekIndex * 7;

        // Account for padded cells at the start / end of the month
        if (cellNum >= firstWeekday && cellNum < paddedDaysInMonth) {
          const button = document.createElement('button');
          const day = cellNum - firstWeekday + 1;
          const date = utils.dateToShortString(new Date(year, month - 1, day));
          const monthString = utils.getMonthString(month);
          const label = `${weekday}, ${monthString} ${day}, ${year}`;

          // Using `add` since IE11 doesn't support `toggle` with second arg
          utils.isTodayString(date) && button.classList.add('today');
          button.setAttribute('tabindex', '-1');
          button.setAttribute('data-date', date);
          button.setAttribute('aria-selected', date === value);
          button.setAttribute('aria-label', label);
          button.setAttribute('type', 'button');
          button.disabled =
            utils.isDateStringOutsideRange(min, max, date) ||
            utils.isDateDisabled(enabledDates, disabledDates, date);
          button.textContent = day;
          button.value = date;
          cell.appendChild(button);
        }
        row.appendChild(cell);
      });
      return row;
    });
  }

  _render(desiredValue) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        // Determine if the value is outside the min/max range, if it is then
        // render the minimum or maximum month, whichever is defined, in that order.
        const value = utils.getClosestAllowedDate(
          this.min,
          this.max,
          desiredValue
        );

        if (utils.isADateString(value)) {
          const fragment = utils.createFragmentWithChildren(
            this._createGridCells(value)
          );

          this._setCalendarMonthDate(value);
          this._updateLabel(value);
          this._updateDates(fragment);
          this._updateControls();
          this._tabDate(
            this._findDate(this.value) ||
              this._findDate(utils.getTodayString()) ||
              this._firstDate
          );

          resolve();
        } else {
          resolve();
        }
      });
    });
  }

  _updateControls() {
    const { min = '', max = '' } = this;
    const { year, month } = utils.parseDateStringToComponents(
      this._calendarMonthDate
    );
    const {
      year: minYear,
      month: minMonth,
    } = utils.parseDateStringToComponents(min);
    const {
      year: maxYear,
      month: maxMonth,
    } = utils.parseDateStringToComponents(max);

    // We don't have accessible properties, yet (https://wicg.github.io/aom/spec/).
    // So store them in a private property for now.
    this._prevMonthBtn._ariaDisabled =
      !!min && minYear >= year && minMonth >= month;
    this._nextMonthBtn._ariaDisabled =
      !!max && maxYear <= year && maxMonth <= month;

    this._prevMonthBtn.setAttribute(
      'aria-disabled',
      this._prevMonthBtn._ariaDisabled
    );
    this._nextMonthBtn.setAttribute(
      'aria-disabled',
      this._nextMonthBtn._ariaDisabled
    );
  }

  _updateLabel(value) {
    this._label.textContent = utils.getLabelFromDate(value);
  }

  _updateDates(fragment) {
    this._dates.innerHTML = '';
    this._dates.appendChild(fragment);
  }

  _handlePrevMonthClick = () => {
    if (!this._prevMonthBtn._ariaDisabled) {
      this._shiftCalendarMonth(-1);
    }
  };

  _handleNextMonthClick = () => {
    if (!this._nextMonthBtn._ariaDisabled) {
      this._shiftCalendarMonth(1);
    }
  };

  _handleDatesKeydown = (event) => {
    const { keyCode } = event;
    if (KEY_CODE_VALUES.includes(keyCode)) {
      event.preventDefault();
    }

    switch (keyCode) {
      case KEY_CODES.ARROW_RIGHT:
        this._shiftCalendarDate(1);
        break;
      case KEY_CODES.ARROW_LEFT:
        this._shiftCalendarDate(-1);
        break;
      case KEY_CODES.ARROW_UP:
        this._shiftCalendarDate(-7);
        break;
      case KEY_CODES.ARROW_DOWN:
        this._shiftCalendarDate(7);
        break;
      case KEY_CODES.ENTER:
      case KEY_CODES.SPACE:
        this._handleDateSelected(event);
        break;
      default:
    }
  };

  _handleDateSelected = ({ target }) => {
    const { value } = target;
    if (value && value !== this.value) {
      this.value = value;
      this._selectDate(target);
      this._focusDate(target);

      this.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          composed: true,
          detail: value,
        })
      );
    }
  };

  _setCalendarMonthDate(value) {
    // Always set the current calendar date to the 1st day of the month
    // so when moving between months we're always working off the same day.
    const { year, month } = utils.parseDateStringToComponents(value);
    this._calendarMonthDate = `${year}-${month}-01`;
  }

  get _firstDate() {
    return this._dates.querySelector('button:not([disabled]):first-of-type');
  }

  get _dateTabIndex() {
    return this.hasAttribute('focus-selected-date') ? '1' : '0';
  }

  get _currentTabbableDate() {
    return this._dates.querySelector(`[tabindex="${this._dateTabIndex}"]`);
  }

  _findDate(date) {
    return this._dates.querySelector(`[data-date="${date}"]:not(:disabled)`);
  }

  _selectDate(element) {
    const selected = this._dates.querySelector('[aria-selected="true"]');
    selected && selected.setAttribute('aria-selected', 'false');
    element.setAttribute('aria-selected', 'true');
  }

  _tabDate(element) {
    const buttons = this._dates.querySelectorAll('button:not([disabled])');
    buttons.forEach((btn) => btn.setAttribute('tabindex', '-1'));
    // if we haven't yet rendered enabled dates, there will be no element,
    // so skip this part - other re-renders could bring back tabindex.
    if (element) {
      element.setAttribute('tabindex', this._dateTabIndex);
    }
  }

  _focusDate(element) {
    this._tabDate(element);
    element.focus();
  }

  _shiftCalendarMonth(direction) {
    const shiftToDate = utils.addToDate(this._calendarMonthDate, {
      months: direction,
    });
    this._render(shiftToDate);
  }

  _shiftCalendarDate(direction) {
    const { value } = this._currentTabbableDate;
    const shiftedDate = utils.addToDate(value, { days: direction });

    const focusDate = () =>
      this._focusDate(this._findDate(shiftedDate) || this._firstDate);
    // Determine if we need to re-render to the next / previous month
    if (!utils.areDateStringsWithinSameMonth(value, shiftedDate)) {
      this._render(shiftedDate).then(focusDate);
    } else {
      focusDate();
    }
  }
}

window.customElements.define('upd-datepicker', Datepicker);
